
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, ReplaySubject } from 'rxjs';
import { environment } from 'src/environments/environment';
import { ApplicationUser, AuthService, ResponseObject, TokenResponse } from './auth.service';
import { ServiceBase } from './service.base';
import { ArtistOverlayDashboardResponse } from '../models/response/artistoverlaydashboard.model';
import { SelectItem } from 'primeng/api';

@Injectable({
  providedIn: 'root'
})
export class ArtistsService extends ServiceBase {

  constructor(private http: HttpClient,
    authService: AuthService) {
    super(authService);
  }

  // Returns all artists (if gallery is logged-in, returns connected artists)
  public getAllArtist(): Observable<Artist[]> {
    const httpOptions = this.RequestHeaders();
    const url = `${environment.BASE_API_PATH}/Artist/fetch`;
    return this.http.get<Artist[]>(url, httpOptions);
  }

  public getArtistsDropdownData(): Observable<SelectItem[]> {
    const httpOptions = this.RequestHeaders();
    const url = `${environment.BASE_API_PATH}/Artist/dropdown`;
    return this.http.get<SelectItem[]>(url, httpOptions);
  }

  
  public getArtistById(artistId: string): Observable<Artist> {
    const httpOptions = this.RequestHeaders();
    const url = `${environment.BASE_API_PATH}/Artist/${encodeURIComponent(artistId)}/fetch`;
    return this.http.get<Artist>(url, httpOptions);
  }

 public getArtistByUserName(username: string): Observable<Artist> {
    const httpOptions = this.RequestHeaders();
    const url = `${environment.BASE_API_PATH}/Artist/username/${username}/fetch`;
    return this.http.get<Artist>(url, httpOptions);
  }


 public getGalleryByuserName(userName: string): Observable<ApplicationUser> {
  const httpOptions = this.RequestHeaders();
  const url = `${environment.BASE_API_PATH}/Gallery/username/${userName}/fetch`;  // <-- ADD "username/"
  return this.http.get<ApplicationUser>(url, httpOptions);
}

  public getGalleryById(galleryId: string): Observable<ApplicationUser> {
    const httpOptions = this.RequestHeaders();
    const url = `${environment.BASE_API_PATH}/Gallery/${encodeURIComponent(galleryId)}`;
    return this.http.get<ApplicationUser>(url, httpOptions);
  }

  public getArtistOverlayPanelHighlights(artistId: string): Observable<ArtistOverlayDashboardResponse> {
    const httpOptions = this.RequestHeaders();
    const url = `${environment.BASE_API_PATH}/Artist/${encodeURIComponent(artistId)}/artist-highlights`;
    return this.http.get<ArtistOverlayDashboardResponse>(url, httpOptions);
  }

  public getArtistArtTypes(): Observable<string[]> {
    const httpOptions = this.RequestHeaders();
    const url = `${environment.BASE_API_PATH}/Artist/arttypes`;
    return this.http.get<string[]>(url, httpOptions);
  }

  public saveArtistArtTypes(artistArtTypeId: string[]): Observable<ResponseObject> {
    const httpOptions = this.RequestHeaders();
    const url = `${environment.BASE_API_PATH}/Artist/saveArtistArtType`;
    return this.http.post<ResponseObject>(url, { artistArtTypeId }, httpOptions);
  }

  public savePrimaryArtTypesId(PrimaryArtTypeId: string): Observable<ResponseObject> {
    const httpOptions = this.RequestHeaders();
    const url = `${environment.BASE_API_PATH}/ArtType/onbording-artType`;
    return this.http.post<ResponseObject>(url, { PrimaryArtTypeId }, httpOptions);
  }

  public getArtistArtTypeDropdownItems(artistId: string): Observable<SelectItem[]> {
    const httpOptions = this.RequestHeaders();
    const url = `${environment.BASE_API_PATH}/Artist/artists/${encodeURIComponent(artistId)}/art-types`;
    return this.http.get<SelectItem[]>(url, httpOptions);
  }

  public saveArtist(artistData: Artist): Observable<ResponseObject> {
    const httpOptions = this.RequestHeaders();
    const url = `${environment.BASE_API_PATH}/Artist/save`;
    return this.http.post<ResponseObject>(url, artistData, httpOptions);
  }

  /**
   * Register a new artist (with optional GalleryEventId for event-only registration)
   * This endpoint returns a TokenResponse with JWT token for auto-login
   * POST /api/Artist/save
   */
  public registerArtistWithAutoLogin(artistData: Artist): Observable<TokenResponse> {
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      }),
      withCredentials: true  // Pass refresh-token cookie
    };

    const url = `${environment.BASE_API_PATH}/Artist/save`;
    const postObservable = this.http.post<TokenResponse>(url, artistData, httpOptions);

    const subject = new ReplaySubject<TokenResponse>(1);
    subject.subscribe((tokenResponse: TokenResponse) => {
      if (tokenResponse.Success && tokenResponse.AccessToken) {
        // Set access token in localStorage
        this.authService.setAccessToken(tokenResponse.AccessToken);
        // Set current user in localStorage
        this.authService.setCurrentUser(tokenResponse.AuthUser);
        // Set session flag
        sessionStorage.setItem('isUserLoggedIn', 'true');

        // Check if this is an event-only artist (GalleryEventId in token)
        const decodedToken = this.authService.decodeToken(tokenResponse.AccessToken);
        if (decodedToken && decodedToken.GalleryEventId) {
          this.authService.setGalleryEventId(decodedToken.GalleryEventId);
        }
      }
    }, (error) => {
      console.error('Registration error:', error);
    });
    postObservable.subscribe(subject);
    return subject;
  }

  public deleteArtist(artistId: string): Observable<ResponseObject> {
    const httpOptions = this.RequestHeaders();
    const url = `${environment.BASE_API_PATH}/Artist/delete/${encodeURIComponent(artistId)}`;
    return this.http.delete<ResponseObject>(url, httpOptions);
  }

  public getExternalUser(userId: string): Observable<ExternalInvitation> {
    const httpOptions = this.RequestHeaders();
    const url = `${environment.BASE_API_PATH}/ExternalInvitation/getExternalUser/${encodeURIComponent(userId)}`;
    return this.http.get<ExternalInvitation>(url, httpOptions);
  }

  public acceptExternalUserRequest(rowId: string): Observable<ResponseObject> {
    const httpOptions = this.RequestHeaders();
    const url = `${environment.BASE_API_PATH}/ExternalInvitation/accept/${encodeURIComponent(rowId)}`;
    return this.http.post<ResponseObject>(url, {}, httpOptions);
  }

 
}


export class Artist {
  Id!: string | null;
  ImgUrl!: string;
  IsInviteAccepted!:boolean
  IsInitiatedByArtist!:boolean
  Name!: string;
  FirstName!: string;
  Email!: string;
  PhoneNumber!: string;
  MobileNumber!: string;
  AddressLine1!: string;
  AddressLine2!: string;
  BankName!: string;
  BankAddress!: string;
  BankBSB!: number;
  AccountNumber!: number;
  LastLoginDate!: Date;
  CreatedDate!: Date;
  ArtType!: string;
  ABN!: string;
  FacebookLink!: string;
  InstagramLink!: string;
  LinkedinLink!: string;
  Website!: string;
  ShortDescription!: string;
  LongDescription!: string;
  SocialMediaDescription!: string;
  BusinessName!: string;
  BusinessNumber!: string;
  BusinessFacebookLink!: string;
  BusinessInstagramLink!: string;
  BusinessLinkedinLink!: string;
  BusinessLogoUrl!: string;
  StreetAddress!: string;
  SubscriptionRuleSetId!:string
  Suburb!: string;
  State!: string;
  PostCode!: string;
  ConfirmPassword!: string
  LastName!: string;
  Password!: string;
  AboutMe?: string;
  Resume?: string;
  Country!: string;
  City!: string;
  Zip!: string;
  PrimaryArtTypeId!:string;
  AccountType: 'Artist' | 'Gallery' = 'Artist';
  CurrencyCode?: string;
  // Other properties
  isSelected?: boolean;
  //logged-in gallery notes about this artist
  ArtistNotes?: string;
  ArtistArtTypes!: ArtistArtType[] ;
  ItemsSoldThisYear?: number;
  //helper properties (related to the connection with the gallery)
  ArtistTurnoverRateInDays?: number;
  InvitationAccepted?: Date;
  ConnectionId!: string;
  ArtistArtType: string[] = [];
  IsProByPlatform?: boolean;
  ProByPlateformDate?: string;
  ProBySource?: string
  PrimaryProductImageUrl!: string;
  UserName!: string;
  ConsignmentDispatchComment!: string;
  GalleryEventId?: string;

}

export class ArtistArtType {
  ArtistId!: string;
  ArtTypeId!: string;
  ArtTypeLabel!: string;
}

export class ExternalInvitation {
  Id!: string;
  ArtistId!: string;
  GalleryId!: string;
  Name!: string;
  Email!: string;
  IsAccepted!: boolean;
  IsInvitedByArtist!: boolean;

  //Helper properties
  InvitedByFullName?: string;
}
